import type { UserProfile } from '@repo/shared/orpc/types';
import { useTheme } from '@repo/theme';
import { BaseTag } from 'component/tag/base-tag';
import { useTranslation } from 'component/translation';
import { MenuItemButton } from 'component/ui/base-menu';
import { BasePopover, type BasePopoverProps } from 'component/ui/base-popover';
import { ProfileButton } from 'component/ui/profile-button';
import { type CSSProperties, useContext } from 'react';
import { Menu as AriaMenu, Dialog, DialogTrigger, type DialogTriggerProps, OverlayTriggerStateContext } from 'react-aria-components';
import { useIsAdmin } from 'util/admin';
import { type TLabel, t } from 'util/stdtypes';

import styles from './profile-menu.module.css';

export type ProfileMenuItem = {
    id: string;
    label: TLabel;
    icon?: React.ComponentType<{ size?: number }>;
    destructive?: boolean;
    testID?: string;
    onAction?: () => void;
};

export type ProfileMenuProps = Pick<DialogTriggerProps, 'defaultOpen' | 'isOpen' | 'onOpenChange'> & {
    profile: UserProfile;
    email?: string;
    roles?: TLabel[];
    items: ProfileMenuItem[];
    /**
     * Admin-only footer block (typically `<FeatureToggles>`) rendered below the
     * action menu inside the dialog popover. The action items still keep their
     * RAC menu semantics (`role=menu` + Arrow-key navigation); the footer lives
     * as a sibling block in the dialog so it can host arbitrary widgets
     * (disclosures, switches, radio groups).
     */
    footer?: React.ReactNode;
    /**
     * Autodocs-only — leave undefined in production. Set `isNonModal: true`
     * on the Primary/autodocs story so the inline docs render doesn't
     * focus-trap or backdrop the docs UI. Production should use the default
     * (modal) behavior — focus trap + outside-click dismiss.
     */
    isNonModal?: BasePopoverProps['isNonModal'];
    placement?: BasePopoverProps['placement'];
    testID?: string;
    /**
     * Inline style forwarded to the popover element — the override channel for
     * popover-level CSS custom properties (e.g. `--popover-min-width`) that a
     * portaled popover can't receive via the cascade. Caller-set values win
     * over `profile-menu.module.css` defaults (inline beats the class).
     */
    popoverStyle?: CSSProperties;
};

function ProfileMenuRow({ item, testID }: { item: ProfileMenuItem; testID: string }) {
    const text = useTranslation(item.label);
    return <MenuItemButton destructive={item.destructive} icon={item.icon} id={item.id} testID={testID} text={text} />;
}

function ProfileInfoHeader({ email, roles }: { email?: string; roles?: TLabel[] }) {
    const isAdmin = useIsAdmin();
    const hasRoles = roles && roles.length > 0;
    const { colors } = useTheme();
    if ((!hasRoles && !email) || !isAdmin) return null;
    return (
        <div className={styles.profileInfo}>
            {hasRoles && (
                <div className={styles.roleTags}>
                    {roles.map((roleLabel) => (
                        <BaseTag
                            backgroundColor={colors.colorTagAccentLight}
                            color={colors.colorTextOnTagLight}
                            key={roleLabel.key ?? roleLabel.defaultText}
                            label={roleLabel}
                            type='small'
                        />
                    ))}
                </div>
            )}
            {email && <div className={styles.email}>{email}</div>}
        </div>
    );
}

/**
 * Dialog content: header + action menu + optional footer. Lives inside `Dialog`
 * so it can access `OverlayTriggerStateContext` to close the popover when an
 * action menu item is pressed (the menu is no longer inside a MenuTrigger, so
 * close-on-select doesn't fire automatically).
 */
function ProfileMenuDialogContent({
    items,
    footer,
    email,
    roles,
    testID,
}: Pick<ProfileMenuProps, 'items' | 'footer' | 'email' | 'roles' | 'testID'>) {
    const state = useContext(OverlayTriggerStateContext);
    const menuLabel = useTranslation(t('profile_menu.menu_label', 'Account'));
    const hasHeader = !!email || (roles !== undefined && roles.length > 0);

    function handleAction(key: React.Key) {
        const item = items.find((i) => i.id === key);
        item?.onAction?.();
        state?.close();
    }

    return (
        <div className={styles.dialogContent}>
            {hasHeader && <ProfileInfoHeader email={email} roles={roles} />}
            <AriaMenu aria-label={menuLabel} autoFocus='first' className={styles.menu} items={items} onAction={handleAction}>
                {(item) => <ProfileMenuRow item={item} testID={item.testID ?? `${testID}-${item.id}`} />}
            </AriaMenu>
            {footer && <div>{footer}</div>}
        </div>
    );
}

/**
 * The popover half of `ProfileMenu`:
 * `<BasePopover><Dialog><ProfileMenuDialogContent/></Dialog></BasePopover>`.
 * Used by `ProfileMenu` itself, and by the rare callsite that needs to pair the
 * canonical popover body with a non-default trigger (e.g. `UserInfo`'s compact
 * mode wrapping a photo-only `AriaButton` instead of `ProfileButton`).
 *
 * Most consumers should reach for `ProfileMenu`, which wraps this with the
 * default Byline-style trigger.
 *
 * @internal
 */
export function ProfileMenuPopover({
    items,
    footer,
    email,
    roles,
    testID,
    placement = 'bottom right',
    popoverStyle,
    isNonModal,
}: Pick<ProfileMenuProps, 'items' | 'footer' | 'email' | 'roles' | 'testID' | 'placement' | 'popoverStyle' | 'isNonModal'>) {
    const dialogLabel = useTranslation(t('profile_menu.dialog_label', 'Profile menu'));
    return (
        <BasePopover isNonModal={isNonModal} offset={5} placement={placement} popoverClassName={styles.popover} style={popoverStyle}>
            <Dialog aria-label={dialogLabel} className={styles.dialog}>
                <ProfileMenuDialogContent email={email} footer={footer} items={items} roles={roles} testID={testID} />
            </Dialog>
        </BasePopover>
    );
}

export function ProfileMenu({
    profile,
    email,
    roles,
    items,
    footer,
    placement = 'bottom right',
    defaultOpen,
    isOpen,
    isNonModal,
    onOpenChange,
    popoverStyle,
    testID = 'profile-menu',
}: ProfileMenuProps) {
    return (
        <DialogTrigger defaultOpen={defaultOpen} isOpen={isOpen} onOpenChange={onOpenChange}>
            <ProfileButton profile={profile} testID={testID} />
            <ProfileMenuPopover
                email={email}
                footer={footer}
                isNonModal={isNonModal}
                items={items}
                placement={placement}
                popoverStyle={popoverStyle}
                roles={roles}
                testID={testID}
            />
        </DialogTrigger>
    );
}
